/**
 * Shared runtime wiring for the generated @hey-api/openapi-ts connectors.
 *
 * Each connector (notifications, dataManagement, ...) gets its own generated
 * `client` instance and a thin per-connector module (e.g. notificationsApi.ts)
 * that sets its base URL and registers interceptors. The cross-cutting pieces
 * are identical across connectors and live here so they are defined once:
 *
 *  - auth: the Cognito id_token is the bearer for every backend;
 *  - 401 handling: a 401 from any backend means the session is gone -> logout;
 *  - error normalization: with `throwOnError` the client rejects with the
 *    parsed error body, not an Error; callers expect an Error with a message.
 *
 * These are plain callbacks rather than a `configure(client)` helper on purpose:
 * each connector's generated `Client` type is a distinct (structurally
 * identical) type, so passing the client in would couple this module to one
 * connector's generated output. Wiring modules call client.setConfig /
 * client.interceptors.*.use themselves and pass these in.
 */
import { getAuthCookie } from 'utils/auth';
import { reportError } from 'utils/reporting';
import { logout } from 'service/cognitoService';

/**
 * Per-request bearer token. The generated runtime calls this for every SDK
 * function whose operation carries `security` metadata, adds the `Bearer `
 * prefix itself, and omits the header when the token is undefined.
 */
export const connectorAuth = (): string | undefined => getAuthCookie()?.id_token;

/**
 * Request interceptor that attaches the Cognito bearer to every request,
 * unconditionally. hey-api's `auth` option only fires for operations whose
 * OpenAPI spec declares `security`; connectors whose spec declares none
 * (DataHub / quart-schema emits no security scheme) would otherwise send no
 * Authorization header and get a 401. Mirrors the legacy connector, which
 * always sent the token. FastAPI connectors keep using `auth` instead.
 */
export const authorizeRequest = (request: Request): Request => {
  const token = connectorAuth();
  if (token) {
    request.headers.set('Authorization', `Bearer ${token}`);
  }
  return request;
};

/**
 * Response interceptor factory: a 401 from any connector means the Cognito
 * session is no longer valid, so report it once and trigger the shared
 * multi-tab logout. The response is returned unchanged. `apiName` only labels
 * the report (e.g. 'Notifications API').
 */
export const createUnauthorizedInterceptor =
  (apiName: string) =>
  (response: Response): Response => {
    if (response.status === 401 && typeof window !== 'undefined') {
      reportError(`${apiName} returned 401; triggering auto-logout`);
      logout();
    }
    return response;
  };

/**
 * Error interceptor for the FastAPI connectors (notifications, dataManagement):
 * on a non-2xx with `throwOnError` the client rejects with the parsed error body
 * (FastAPI's `{ detail }`) or raw text, not an Error. Normalize to a real Error
 * so callers can rely on `err instanceof Error` and a readable `err.message`.
 */
export const normalizeFastApiError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }
  if (typeof error === 'object' && error !== null && 'detail' in error) {
    const { detail } = error as { detail: unknown };
    return new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  return new Error(typeof error === 'string' ? error : JSON.stringify(error));
};

/**
 * Error interceptor for the DataHub connector. DataHub is a Quart/quart-schema
 * service whose error bodies are `{ message }` — its custom exception and
 * validation handlers, and every route, emit `message` (never FastAPI's
 * `{ detail }`). Normalize to a real Error carrying that message, matching what
 * the legacy DataHub connector surfaced.
 */
export const normalizeDataHubApiError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const { message } = error as { message: unknown };
    return new Error(typeof message === 'string' ? message : JSON.stringify(message));
  }
  return new Error(typeof error === 'string' ? error : JSON.stringify(error));
};
